// src/hooks/useListasTrabajo.js 

import { useState, useEffect, useCallback, useRef } from 'react';
import listasTrabajoService from '../services/listasTrabajoService';
import { useNotification } from '../contexts/NotificationContext'; 

const useListasTrabajo = (tipoExpediente) => {
    const { showSuccess, showError, showWarning } = useNotification(); 

    const [listas, setListas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Evita setState después de un unmount
    const aliveRef = useRef(true);

    useEffect(() => {
        aliveRef.current = true; // Reset en StrictMode re-mount
        return () => { aliveRef.current = false; };
    }, []);

    const cargarListas = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await listasTrabajoService.obtenerListas(tipoExpediente);
            if (!aliveRef.current) return;
            setListas(data || []);
        } catch (err) {
            if (!aliveRef.current) return;
            console.error('Error al cargar listas:', err);
            setError(err.message);
            showError('Error al cargar listas: ' + err.message); // ✨ SNACKBAR
            setListas([]);
        } finally {
            if (aliveRef.current) setLoading(false);
        }
    }, [tipoExpediente, showError]); // ✅ AGREGAR showError a deps

    useEffect(() => {
        cargarListas();
    }, [cargarListas]);

    const crearLista = async (datos) => {
        try {
            const resp = await listasTrabajoService.crearLista({
                ...datos,
                tipoExpediente,
            });
            // El backend devuelve { ok, id, lista, mensaje } — usamos `lista`
            // (la lista completa con Nombre, Color, etc.) y solo si viene OK.
            const nuevaLista = resp?.lista || null;
            if (nuevaLista) {
                setListas((prev) => [nuevaLista, ...prev]);
            } else {
                // Fallback: si por algún motivo no vino la lista, recargar todo
                await cargarListas();
            }
            showSuccess('Lista creada correctamente');
            return nuevaLista || resp;
        } catch (err) {
            console.error('Error al crear lista:', err);
            showError('Error al crear lista: ' + err.message); // ✨ SNACKBAR
            throw err;
        }
    };

    const actualizarLista = async (idLista, datos) => {
        try {
            await listasTrabajoService.actualizarLista(idLista, datos);
            setListas((prev) =>
                prev.map((lista) => (lista.Id === idLista ? { ...lista, ...datos } : lista)),
            );
            showSuccess('Lista actualizada correctamente'); // ✨ SNACKBAR
        } catch (err) {
            console.error('Error al actualizar lista:', err);
            showError('Error al actualizar lista: ' + err.message); // ✨ SNACKBAR
            throw err;
        }
    };

    const eliminarLista = async (idLista) => {
        // ✅ SIN window.confirm - se maneja en el componente
        try {
            await listasTrabajoService.eliminarLista(idLista);
            setListas((prev) => prev.filter((lista) => lista.Id !== idLista));
            showSuccess('Lista eliminada correctamente');
        } catch (err) {
            console.error('Error al eliminar lista:', err);
            showError('Error al eliminar lista: ' + err.message);
            throw err;
        }
    };

    const agregarExpedientes = async (idLista, expedientesIds) => {
        try {
            const response = await listasTrabajoService.agregarExpedientes(idLista, expedientesIds);
            setListas((prev) =>
                prev.map((lista) =>
                    lista.Id === idLista
                        ? { ...lista, Total_Expedientes: (lista.Total_Expedientes || 0) + response.agregados }
                        : lista,
                ),
            );

            // ✅ CORRECCIÓN: Sintaxis correcta de template literal
            if (response.duplicados > 0) {
                showWarning(`${response.agregados} expedientes agregados. ${response.duplicados} ya estaban en la lista.`); // ✨ SNACKBAR
            } else {
                showSuccess(`${response.agregados} expedientes agregados correctamente`); // ✨ SNACKBAR
            }

            return response;
        } catch (err) {
            console.error('Error al agregar expedientes:', err);
            showError('Error al agregar expedientes: ' + err.message); // ✨ SNACKBAR
            throw err;
        }
    };

    const duplicarLista = async (idLista, nuevoNombre) => {
        try {
            // Obtener la lista original y crear una copia
            const original = listas.find(l => l.Id === idLista);
            if (!original) throw new Error('Lista no encontrada');
            const nuevaLista = await listasTrabajoService.crearLista({
                nombre: nuevoNombre || `${original.Nombre} (copia)`,
                descripcion: original.Descripcion,
                tipoExpediente,
                color: original.Color,
                icono: original.Icono,
            });
            showSuccess('Lista duplicada correctamente');
            return nuevaLista;
        } catch (err) {
            console.error('Error al duplicar lista:', err);
            showError('Error al duplicar lista: ' + err.message);
            throw err;
        }
    };

    const toggleFavorita = async (idLista, favoritaActual) => {
        try {
            await listasTrabajoService.toggleFavorita(idLista, !favoritaActual);
            await cargarListas();
        } catch (error) {
            showError('Error al cambiar favorita');
        }
    };

    const exportarLista = async (idLista) => {
        showWarning('Función de exportación no disponible aún');
    };

    return {
        listas,
        loading,
        error,
        cargarListas,
        crearLista,
        actualizarLista,
        eliminarLista,
        agregarExpedientes,
        duplicarLista,
        exportarLista,
        toggleFavorita,
    };
};

export default useListasTrabajo;